import { __awaiter, __decorate, __generator, __metadata, __param } from "tslib";
import { injectable, inject } from 'inversify';
import { isAxiosError } from 'axios';
import { ModalUtils } from './Modal/ModalUtils';
import NeoTypes from '../AppServices/NeoTypes';
import NeoStrings from '../Localisation/NeoStrings';
import AxiosUtils from "../AxiosUtils";
export var ErrorDisplayType;
(function (ErrorDisplayType) {
    /** Show the error text in a modal. */
    ErrorDisplayType[ErrorDisplayType["ShowModal"] = 0] = "ShowModal";
    /** Show the error text in the global notifications. */
    ErrorDisplayType[ErrorDisplayType["ShowNotification"] = 1] = "ShowNotification";
    /** Do nothing, error will be re-thrown. */
    ErrorDisplayType[ErrorDisplayType["ThrowError"] = 2] = "ThrowError";
})(ErrorDisplayType || (ErrorDisplayType = {}));
var ErrorHandler = /** @class */ (function () {
    function ErrorHandler(notifications) {
        this.notifications = notifications;
    }
    ErrorHandler.prototype.showError = function (error, displayType) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!(displayType !== ErrorDisplayType.ThrowError)) return [3 /*break*/, 2];
                        _a = this.displayError;
                        _b = [displayType, error];
                        return [4 /*yield*/, this.parseError(error)];
                    case 1:
                        _a.apply(this, _b.concat([_c.sent()]));
                        _c.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    ErrorHandler.prototype.parseError = function (error) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!isAxiosError(error)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.getAxiosError(error, error.response)];
                    case 1: return [2 /*return*/, _a.sent()];
                    case 2: return [2 /*return*/, this.getGeneralError(error)];
                }
            });
        });
    };
    ErrorHandler.prototype.getAxiosError = function (error, response) {
        return __awaiter(this, void 0, void 0, function () {
            var errorDisplay, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!response) return [3 /*break*/, 12];
                        _a = response.status;
                        switch (_a) {
                            case 400: return [3 /*break*/, 1];
                            case 401: return [3 /*break*/, 3];
                            case 403: return [3 /*break*/, 5];
                            case 409: return [3 /*break*/, 7];
                        }
                        return [3 /*break*/, 9];
                    case 1: return [4 /*yield*/, this.get400Error(error)];
                    case 2:
                        errorDisplay = _b.sent();
                        return [3 /*break*/, 11];
                    case 3: return [4 /*yield*/, this.get401Error(error)];
                    case 4:
                        errorDisplay = _b.sent();
                        return [3 /*break*/, 11];
                    case 5: return [4 /*yield*/, this.get403Error(error)];
                    case 6:
                        errorDisplay = _b.sent();
                        return [3 /*break*/, 11];
                    case 7: return [4 /*yield*/, this.get409Error(error)];
                    case 8:
                        errorDisplay = _b.sent();
                        return [3 /*break*/, 11];
                    case 9: return [4 /*yield*/, this.parseAxiosError(error.message, error.message, error)];
                    case 10:
                        errorDisplay = _b.sent();
                        errorDisplay.isServerError = true;
                        _b.label = 11;
                    case 11: return [3 /*break*/, 13];
                    case 12:
                        errorDisplay = this.getGeneralError(error);
                        // Add status code of -1 to differentiate network errors from code exceptions.
                        errorDisplay.statusCode = -1;
                        errorDisplay.isNetworkError = true;
                        _b.label = 13;
                    case 13: return [2 /*return*/, errorDisplay];
                }
            });
        });
    };
    ErrorHandler.prototype.get400Error = function (error) {
        var _a;
        var isGet = ((_a = error.response) === null || _a === void 0 ? void 0 : _a.config.method) === "get";
        return this.parseAxiosError(NeoStrings.errors.invalidDataTitle, isGet ? NeoStrings.errors.invalidDataBodyGet : NeoStrings.errors.invalidDataBody, error);
    };
    ErrorHandler.prototype.get401Error = function (error) {
        return this.parseAxiosError(NeoStrings.errors.notAuthenticatedTitle, NeoStrings.errors.notAuthenticatedBody, error);
    };
    ErrorHandler.prototype.get403Error = function (error) {
        return this.parseAxiosError(NeoStrings.errors.unAuthorisedTitle, NeoStrings.errors.unAuthorisedBody, error);
    };
    ErrorHandler.prototype.get409Error = function (error) {
        return this.parseAxiosError(NeoStrings.errors.concurrencyTitle, NeoStrings.errors.concurrencyBody, error);
    };
    /**
     * Parses an axios response into an IErrorDisplay object.
     * @param header Header to include in result.
     * @param body Body to include if the responses title is missing.
     * @param error Axios error.
     */
    ErrorHandler.prototype.parseAxiosError = function (header, body, error) {
        return __awaiter(this, void 0, void 0, function () {
            var errorDisplay, rawErrorData_1, errorData_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        errorDisplay = { header: header, body: body, statusText: error.message, statusCode: (_a = error.response) === null || _a === void 0 ? void 0 : _a.status, error: error };
                        if (!error.response.data) return [3 /*break*/, 2];
                        return [4 /*yield*/, AxiosUtils.ensureAxiosErrorData(error.response.data)];
                    case 1:
                        rawErrorData_1 = _b.sent();
                        if (rawErrorData_1.title) {
                            errorDisplay.body = rawErrorData_1.title;
                            rawErrorData_1 = rawErrorData_1.errors;
                        }
                        if (rawErrorData_1) {
                            if (typeof rawErrorData_1 === "string") {
                                errorDisplay.body = rawErrorData_1;
                            }
                            else {
                                errorData_1 = { errors: [], properties: [] };
                                errorDisplay.data = errorData_1;
                                if (rawErrorData_1[""]) {
                                    errorData_1.errors = rawErrorData_1[""];
                                }
                                Object.keys(rawErrorData_1).forEach(function (key) {
                                    if (key !== "$id" && key !== "") {
                                        var propertyErrors = rawErrorData_1[key];
                                        if (propertyErrors && propertyErrors instanceof Array) {
                                            errorData_1.properties.push({ key: key, errors: propertyErrors.join(", ") });
                                        }
                                    }
                                });
                            }
                        }
                        _b.label = 2;
                    case 2: return [2 /*return*/, errorDisplay];
                }
            });
        });
    };
    ErrorHandler.prototype.getGeneralError = function (error) {
        var errorDescription = error.message ? error.message : error;
        return { header: NeoStrings.errors.generalTitle, body: errorDescription, error: error };
    };
    ErrorHandler.prototype.displayError = function (displayType, error, errorDisplay) {
        if (error instanceof Error) {
            console.log(error.message);
            console.log(error.stack);
        }
        if (displayType === ErrorDisplayType.ShowModal) {
            this.displayModal(error, errorDisplay);
        }
        else {
            this.displayNotification(error, errorDisplay);
        }
    };
    ErrorHandler.prototype.displayModal = function (error, errorDisplay) {
        ModalUtils.showMessage(errorDisplay.header, errorDisplay.body);
    };
    ErrorHandler.prototype.displayNotification = function (error, errorDisplay) {
        this.notifications.addDanger(errorDisplay.header, errorDisplay.body);
    };
    ErrorHandler = __decorate([
        injectable(),
        __param(0, inject(NeoTypes.UI.GlobalNotifications.symbol)),
        __metadata("design:paramtypes", [Object])
    ], ErrorHandler);
    return ErrorHandler;
}());
export { ErrorHandler };
